import { CurrencyPoundRounded } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { useState, useEffect } from 'react'
import {
  DateTimeInput,
  EditBase,
  Form,
  NumberInput,
  SelectInput,
  TextInput,
  useNotify,
  useRecordContext,
  useRefresh,
  WithRecord,
  useGetList,
} from 'react-admin'
import { validateAutoSaveSingleInput } from '../../../../Utils/AutoSave'
import ArchiveLeadButton from '../../Actions/ArchiveLeadButton'
import DuplicateLeadButton from '../../Actions/DuplicateLeadButton'
import LeadSourceButton from '../../Actions/LeadSourceButton'
import MTButton from '../../Actions/MTButton'
import MarkScamButton from '../../Actions/MarkScamButton'
import DateTimePickerValue from '../LatestContact'
import { sendLog } from '../../../../Class/AuditLog'
import {
  getAdminUsers,
  handleLeadLocking,
  reassignMainUser,
} from '../../../../Utils/RestUtils'
import { DEFAULT_STATUS_LIST } from '../../../../Utils/ConstantsUtils'
import { reassignBookingAgent } from '../../../../Utils/QuoteRequestUtils'

const LeadIDPanel = () => {
  const record = useRecordContext()
  const notify = useNotify()
  const refresh = useRefresh()
  const [tooGoodToScrap, setTooGoodToScrap] = useState()
  const [initialLoad, setInitialLoad] = useState(true)
  const [adminUsers, setAdminUsers] = useState()
  const [mainUser, setMainUser] = useState(record?.mainUser?.id)
  const user_id = localStorage.getItem('user_id')

  const tooGood = record.leadContact.vehicle.tooGoodToScrap

  if (tooGood !== tooGoodToScrap) {
    setTooGoodToScrap(tooGood)
  }

  const { data: users, isLoading } = useGetList('users', {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: 'id', order: 'ASC' },
    filter: { disabled: 0 },
  })

  const handleAssignedToChange = async (e) => {
    let result = await reassignBookingAgent({
      quoteId: record['@id'],
      userId: e.target.value,
    })

    if (result.status === 'success') {
      notify(result.message, { type: 'success' })
      refresh()
    } else {
      notify(result.error_msg, { type: 'error' })
    }
  }

  const handleMainUserChange = async (event) => {
    setMainUser(event.target.value)
    await reassignMainUser(record.originId, event.target.value)
  }

  const permissions = (localStorage.getItem('permissions') || '').split(',')

  const isNotificationsUser = permissions.includes('ROLE_SALVAGE_NOTIFICATIONS')
  const isUserASuperAdmin =
    (permissions.includes('ROLE_ADMIN') && user_id != 18) || user_id == 12
  const canSeeMTButton = ![
    'ROLE_EXTERNAL_SCRAP',
    'ROLE_SALES_EXEC',
    'ROLE_TEAM_LEAD',
    'ROLE_EXTERNAL_SALVAGE',
  ].some((role) => permissions.includes(role))

  useEffect(() => {
    // Show getNext users a toast notification for 'Offer accepted'
    if (record.status === 'Offer accepted' && !isUserASuperAdmin) {
      notify('This lead is in Offer Accepted! 🎉', {
        type: 'success',
      })
    }

    if (record.status === 'Full form completed' && !isUserASuperAdmin) {
      notify('This customer completed the full journey! 🎉', {
        type: 'success',
      })
    }

    if (record.status === 'New inquiry' && isNotificationsUser) {
      notify('This is a new lead, not a drop-down', {
        type: 'success',
      })
    }

    const vehicle = record.leadContact?.vehicle
    const vehicleSalvage = record.leadContact?.vehicle?.vehicleSalvage
    const currentCapValue = vehicleSalvage?.currentCapValue

    if (
      vehicle &&
      vehicle.tooGoodToScrap &&
      vehicleSalvage &&
      (currentCapValue == null || Number(currentCapValue) === 0)
    ) {
      notify('Requires Manual Cap - please speak to a Team lead', {
        type: 'warning',
      })
    }

    // Lock lead here
    if (user_id != 17) {
      if (record) {
        if (record.inUse && record.inUse.id != user_id && !isUserASuperAdmin) {
          notify(
            'This lead is already in use by: ' + record.inUse.displayName,
            { type: 'error' }
          )
          location.href = isUserASuperAdmin ? '/#/quotes' : '/#/'
        } else {
          handleLeadLocking('lock', record.originId)
          sendLog(record.originId, 'Shown Lead')
          if (!adminUsers && initialLoad) {
            const getSelectOptions = async () => {
              const admins = await getAdminUsers()
              setAdminUsers(admins)
            }

            getSelectOptions()
          }
          setInitialLoad(false)

          return () => {
            sendLog(record.originId, 'Logged out of lead')
            handleLeadLocking('unlock', record.originId)
          }
        }
      }
    }
  }, [])

  let assigned =
    record.leadContact.vehicle.vehicleCollection &&
    record.leadContact.vehicle.vehicleCollection.offerSelected &&
    record.leadContact.vehicle.vehicleCollection.offerSelected.collector

  const assignedStyle = {
    backgroundColor: 'green',
    color: '#FFFFFF',
    borderColor: '#FFFFFF',
  }

  return (
    <EditBase>
      <Form>
        <Grid container spacing={1} sx={{ marginBottom: '2px' }}>
          <Grid item flex={2}>
            <DuplicateLeadButton />
          </Grid>
          <Grid item flex={1}>
            {assigned && isUserASuperAdmin && (
              <FormControl sx={{ width: '100%' }} size="small">
                <InputLabel id="approved-by-label">Assigned to</InputLabel>
                <Select
                  labelId="approved-by-label"
                  name="leadContact.vehicle.vehicleCollection
                          .assignedByAdmin"
                  defaultValue="default"
                  label="Assigned to"
                  value={
                    record.leadContact.vehicle.vehicleCollection
                      ? record.leadContact.vehicle.vehicleCollection
                          .assignedByAdmin['@id']
                      : 'default'
                  }
                  onChange={handleAssignedToChange}
                >
                  <MenuItem disabled value="default">
                    AssignedTo?
                  </MenuItem>
                  {adminUsers &&
                    adminUsers.map((user) => {
                      return (
                        <MenuItem key={user.id} value={user.id}>
                          {user.name}
                        </MenuItem>
                      )
                    })}
                </Select>
              </FormControl>
            )}
            {!assigned && (
              <Button onClick={refresh} sx={{ mb: 1.5 }} fullWidth>
                Update
              </Button>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid item flex={1}>
            <TextInput
              label="Quote ID"
              readOnly={true}
              disabled={true}
              source="originId"
              onBlur={async (e) =>
                await validateAutoSaveSingleInput(
                  record,
                  e.target,
                  notify,
                  refresh
                )
              }
            />
          </Grid>
          <Grid item flex={1}>
            <NumberInput
              disabled={true}
              source="leadContact.vehicle.vehicleSalvage.chosenValue"
              InputProps={{
                startAdornment: (
                  <InputAdornment position={'start'}>
                    <CurrencyPoundRounded fontSize="small" />
                  </InputAdornment>
                ),
              }}
              label="Price"
              defaultValue="0"
              onBlur={async (e) =>
                await validateAutoSaveSingleInput(
                  record,
                  e.target,
                  notify,
                  refresh
                )
              }
            />
          </Grid>
          <Grid item flex={1}>
            <NumberInput
              disabled={true}
              source="leadContact.vehicle.vehicleSalvage.calcCommission"
              InputProps={{
                startAdornment: (
                  <InputAdornment position={'start'}>
                    <CurrencyPoundRounded fontSize="small" />
                  </InputAdornment>
                ),
              }}
              label="Commission"
              onBlur={async (e) =>
                await validateAutoSaveSingleInput(
                  record,
                  e.target,
                  notify,
                  refresh
                )
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid item flex={1}>
            <FormControl sx={{ width: '100%' }} size="small">
              <SelectInput
                alwaysOn
                select
                key={2}
                label="Lead Status"
                source="status"
                name="status"
                placeholder="Select a status"
                choices={DEFAULT_STATUS_LIST}
                onChange={async (e) =>
                  await validateAutoSaveSingleInput(
                    record,
                    e.target,
                    notify,
                    refresh
                  )
                }
              />
            </FormControl>
          </Grid>
          {isUserASuperAdmin && (
            <Grid item flex={1}>
              <FormControl sx={{ width: '100%', my: 1 }} size="small">
                <InputLabel id="main-user-label">Main User</InputLabel>
                <Select
                  name="mainUser"
                  labelId="main-user-label"
                  value={mainUser ?? 'null'}
                  label="Main User"
                  onChange={handleMainUserChange}
                  disabled={isLoading}
                  endAdornment={
                    isLoading && (
                      <InputAdornment position="start">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    )
                  }
                >
                  <MenuItem value="null">
                    <em>None</em>
                  </MenuItem>
                  {users?.map((user) => (
                    <MenuItem key={user.id} value={user.originId}>
                      {user.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item flex={1}>
            <Box sx={{ my: 1 }}>
              <DateTimeInput
                label="Lead Created"
                readOnly={true}
                disabled={true}
                source="dateCreated"
                onBlur={async (e) =>
                  await validateAutoSaveSingleInput(
                    record,
                    e.target,
                    notify,
                    refresh
                  )
                }
              />
            </Box>
          </Grid>
        </Grid>
        <Grid item flex={1}>
          <DateTimePickerValue />
        </Grid>
        <Grid container spacing={1}>
          <Grid item flex={1}>
            <TextInput
              label="Lead Source"
              readOnly={true}
              disabled={true}
              source="leadContact.leadContactAttributes.leadSource.title"
              onBlur={async (e) =>
                await validateAutoSaveSingleInput(
                  record,
                  e.target,
                  notify,
                  refresh
                )
              }
            />
          </Grid>
          <Grid item flex={1}>
            <WithRecord
              label="Assigned To"
              render={(record) => {
                let render = true
                let value = ''
                let style = {}
                if (assigned && render) {
                  render = false
                  value =
                    record.leadContact.vehicle.vehicleCollection.offerSelected
                      .collector.collectionAgentCompanyName
                  style = assignedStyle
                }

                return (
                  <TextField
                    key={1}
                    label="Assigned To"
                    readOnly={true}
                    disabled={true}
                    value={value}
                    InputProps={{ style: style }}
                  />
                )
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1} sx={{ marginTop: '-4px' }}>
          <Grid item flex={2}>
            <LeadSourceButton />
          </Grid>
          <Grid item flex={2}>
            <ArchiveLeadButton />
          </Grid>
          <Grid item flex={2}>
            <Grid container spacing={1}>
              <Grid item flex={1}>
                <MarkScamButton />
              </Grid>
              {canSeeMTButton && (
                <Grid item flex={1}>
                  <MTButton />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Form>
    </EditBase>
  )
}

export default LeadIDPanel
